import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { DataTable } from "../../components/DataTable/DataTable";
import { useCollection } from "../../hooks/useCollection";
import {
  getPlans,
  addPlan,
  updatePlan,
  deletePlan,
} from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { logUserActivity } from "../../utils/activityLogger";
import { validatePlan, featuredStateWarning } from "../../utils/planValidation";
import { money } from "../../utils/queuePresentation";

const dialogFieldSx = {
  borderRadius: "8px",
  "& .MuiInputBase-root": { borderRadius: "6px" },
};

const actionButtonSx = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };

const emptyForm = { hours: 1, price: 0, featured: false };

// Plans have no "name" field — this is the human-readable stand-in used in
// audit-log entries and the delete-confirm prompt. Prices are £ (Stripe), at most
// two decimals like every amount (047 D79).
const price = (p) => `£${money(p?.price)}`;
const planLabel = (p) => `${p.hours} sessions - ${price(p)}`;

const Plans = () => {
  const { user } = useAuth();
  const userRole = user?.role || "";

  const fetchPlans = useCallback(() => getPlans(), []);
  const { data, loading, error, refetch } = useCollection(fetchPlans);

  // Add/Edit dialog — one dialog, driven by dialogMode.
  const [dialogMode, setDialogMode] = useState(null); // null | "add" | "edit"
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete confirm dialog.
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const showError = (message) => setSnackbar({ open: true, message });
  const closeSnackbar = () => setSnackbar({ open: false, message: "" });

  const auditActor = () => ({
    userId: user?.uid || user?.id || "unknown",
    userName: user?.name || user?.displayName || "Unknown User",
    userEmail: user?.email || "unknown@example.com",
  });

  const openAdd = () => {
    setEditingRow(null);
    setFormData(emptyForm);
    setFormErrors({});
    setDialogMode("add");
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setFormData({
      hours: row.hours ?? 1,
      price: row.price ?? 0,
      featured: Boolean(row.featured),
    });
    setFormErrors({});
    setDialogMode("edit");
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogMode(null);
  };

  const handleSubmit = async () => {
    const errors = validatePlan({
      hours: formData.hours,
      price: formData.price,
    });
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const { userId, userName, userEmail } = auditActor();
    const after = {
      hours: formData.hours,
      price: formData.price,
      featured: formData.featured,
    };
    setSaving(true);
    try {
      if (dialogMode === "add") {
        const ref = await addPlan(after);
        await logUserActivity({
          userId,
          userName,
          userEmail,
          action: "ADD_PLAN",
          entityType: "plan",
          entityId: ref.id,
          entityName: planLabel(after),
          before: {},
          after,
          description: `${userName} added plan: ${planLabel(after)}`,
        });
      } else {
        const before = {
          hours: editingRow.hours,
          price: editingRow.price,
          featured: Boolean(editingRow.featured),
        };
        await updatePlan(editingRow.id, after);
        await logUserActivity({
          userId,
          userName,
          userEmail,
          action: "UPDATE_PLAN",
          entityType: "plan",
          entityId: editingRow.id,
          entityName: planLabel(after),
          before,
          after,
          description: `${userName} updated plan: ${planLabel(after)}`,
        });
      }
      setDialogMode(null);
      await refetch();
    } catch (e) {
      showError(e.message || "Failed to save plan.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow) return;
    const { userId, userName, userEmail } = auditActor();
    setDeleting(true);
    try {
      await deletePlan(deleteRow.id);
      await logUserActivity({
        userId,
        userName,
        userEmail,
        action: "DELETE_PLAN",
        entityType: "plan",
        entityId: deleteRow.id,
        entityName: planLabel(deleteRow),
        before: {
          hours: deleteRow.hours,
          price: deleteRow.price,
          featured: Boolean(deleteRow.featured),
        },
        after: {},
        description: `${userName} deleted plan: ${planLabel(deleteRow)}`,
      });
      setDeleteRow(null);
      await refetch();
    } catch (e) {
      showError(e.message || "Failed to delete plan.");
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { key: "hours", label: "Sessions", sortable: true },
    {
      key: "price",
      label: "Price",
      sortable: true,
      render: (row) => price(row),
    },
    {
      key: "featured",
      label: "Featured",
      render: (row) =>
        row.featured ? (
          <Chip label="Most popular" size="small" color="primary" />
        ) : (
          "—"
        ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) =>
        hasPermission(userRole, permissions.EDIT_PLANS) && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => openEdit(row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteRow(row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
    },
  ];

  // data starts as [] before the first fetch resolves — judge the featured
  // count only once real data is in, or the loading flash would misreport
  // "no plan is featured".
  const warning =
    !loading && !error && data.length > 0 ? featuredStateWarning(data) : null;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
          Plans
        </Typography>
      </Box>

      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      )}

      <DataTable
        columns={columns}
        rows={data}
        initialSort={{ field: "hours", direction: "asc" }}
        loading={loading}
        error={error}
        emptyText="No plans yet."
        headerActions={
          hasPermission(userRole, permissions.EDIT_PLANS) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                px: 2,
                height: "40px",
                boxShadow: "none",
              }}
            >
              Add Plan
            </Button>
          )
        }
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={Boolean(dialogMode)}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>
          {dialogMode === "add" ? "Add Plan" : "Edit Plan"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            type="number"
            label="Sessions"
            fullWidth
            margin="normal"
            value={formData.hours}
            onChange={(e) =>
              setFormData({ ...formData, hours: Number(e.target.value) })
            }
            error={Boolean(formErrors.hours)}
            helperText={
              formErrors.hours || "Number of sessions this pack includes"
            }
            inputProps={{ step: 1, min: 1 }}
            sx={dialogFieldSx}
          />
          <TextField
            type="number"
            label="Price (£)"
            fullWidth
            margin="normal"
            value={formData.price}
            onChange={(e) =>
              setFormData({ ...formData, price: Number(e.target.value) })
            }
            error={Boolean(formErrors.price)}
            helperText={formErrors.price || "Price in GBP, e.g. 49.99"}
            inputProps={{ step: 0.01, min: 0 }}
            sx={dialogFieldSx}
          />
          <FormControlLabel
            sx={{ mt: 1, ml: 0 }}
            control={
              <Switch
                checked={formData.featured}
                onChange={(e) =>
                  setFormData({ ...formData, featured: e.target.checked })
                }
              />
            }
            label='Featured (shows the "Most popular" ribbon in the app)'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving} sx={actionButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
            sx={actionButtonSx}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={Boolean(deleteRow)}
        onClose={() => !deleting && setDeleteRow(null)}
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>Delete Plan</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Delete the {deleteRow?.hours}-session plan ({price(deleteRow)})?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteRow(null)}
            disabled={deleting}
            sx={actionButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            sx={actionButtonSx}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Plans;
